/* GUF Assets 2.0. Progressive enhancement for native Shopify forms and routes. */
(() => {
  'use strict';
  const documentRoot = document.documentElement;
  documentRoot.classList.replace('no-js', 'js');
  const configNode = document.getElementById('GufConfig');
  const config = configNode ? JSON.parse(configNode.textContent) : {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initialized = new WeakSet();
  let cartBusy = false;
  let searchRequest = null;
  let searchTimer;
  const parseHTML = html => new DOMParser().parseFromString(html, 'text/html');
  const announce = message => {
    const status = document.getElementById('GufStatus');
    if (status) status.textContent = message;
  };
  const errorMessage = (error, fallback) => typeof error?.message === 'string' ? error.message : fallback;
  async function request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let payload;
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('json')) payload = await response.json();
    else payload = await response.text();
    if (!response.ok || (payload && typeof payload === 'object' && payload.status >= 400)) {
      const message = typeof payload === 'object' ? payload.description || payload.message : null;
      throw new Error(typeof message === 'string' ? message : config.error || 'Please try again.');
    }
    return payload;
  }
  function openDialog(id, trigger) {
    const dialog = document.getElementById(id);
    if (!dialog || !dialog.showModal) return false;
    document.querySelectorAll('dialog[open]').forEach(other => { if (other !== dialog) other.close(); });
    dialog._returnFocus = trigger || document.activeElement;
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('modal-open');
    if (id === 'SearchDialog') dialog.querySelector('input[type=search]')?.focus();
    else dialog.querySelector('[data-dialog-close]')?.focus();
    return true;
  }
  function initializeDialog(dialog) {
    if (initialized.has(dialog)) return;
    initialized.add(dialog);
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('video,audio').forEach(media => media.pause());
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
      if (dialog._returnFocus?.isConnected) dialog._returnFocus.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
  }
  function showError(container, selector, message) {
    const node = container?.querySelector(selector);
    if (!node) { announce(message); return; }
    node.hidden = !message;
    node.textContent = message || '';
  }
  function renderCart(html) {
    if (typeof html !== 'string') return false;
    const updated = parseHTML(html).getElementById('CartDialog');
    const current = document.getElementById('CartDialog');
    if (!updated || !current) return false;
    const wasOpen = current.open;
    current.innerHTML = updated.innerHTML;
    current.dataset.cartRenderCount = updated.dataset.cartRenderCount;
    const count = updated.dataset.cartRenderCount || '0';
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count; });
    document.querySelectorAll('[data-cart-open]').forEach(node => { node.setAttribute('aria-label', `Cart, ${count} items`); });
    if (wasOpen) current.querySelector('[data-dialog-close]')?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('guf:cart:updated', { detail: { itemCount: Number(count) } }));
    return true;
  }
  async function refreshCart() {
    const url = new URL(config.cart, window.location.origin);
    url.searchParams.set('section_id', 'guf-cart-drawer');
    const html = await request(url.toString(), { headers: { Accept: 'text/html' }, cache: 'no-store' });
    if (!renderCart(html)) throw new Error(config.cartError);
  }
  async function syncCart(result) {
    if (!renderCart(result?.sections?.['guf-cart-drawer'])) await refreshCart();
  }
  async function offerProducts(trigger) {
    const dialog = document.getElementById('GufOffers');
    const form = dialog?.querySelector('[data-offer-form]');
    if (!dialog?.showModal || !form) return;
    const inCart = new Set([...document.querySelectorAll('#CartDialog [data-cart-product-id]')].map(node => node.dataset.cartProductId));
    let visible = 0;
    const limit = Number(dialog.dataset.offerLimit) || 3;
    const cards = [...dialog.querySelectorAll('[data-offer-card]')];
    cards.forEach(card => {
      const input = card.querySelector('input');
      input.checked = false;
      card.hidden = inCart.has(card.dataset.productId) || visible >= limit;
      input.disabled = card.hidden;
      if (!card.hidden) visible++;
    });
    if (!visible) return;
    dialog.style.setProperty('--offer-columns', String(Math.min(visible, 3)));
    const submit = form.querySelector('[data-offer-submit]');
    const skip = form.querySelector('[data-offer-skip]');
    const total = form.querySelector('[data-offer-total]');
    const message = form.querySelector('[data-offer-error-message]');
    const close = dialog.querySelector('[data-dialog-close]');
    const originalLabel = submit.textContent;
    let pending = false;
    let uncertain = false;
    const selected = () => [...form.querySelectorAll('input[name=offer]:checked:not(:disabled)')];
    function update() {
      const choices = selected();
      const cents = choices.reduce((sum, input) => sum + Number(input.dataset.offerPrice) * Number(input.dataset.offerQuantity), 0);
      total.textContent = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
      submit.disabled = pending || uncertain || choices.length === 0;
    }
    message.hidden = true;
    message.textContent = '';
    update();
    return new Promise(resolve => {
      const controller = new AbortController();
      const opts = { signal: controller.signal };
      function finish() {
        controller.abort();
        form.removeAttribute('aria-busy');
        submit.textContent = originalLabel;
        close.disabled = false;
        skip.disabled = false;
        resolve();
      }
      dialog.addEventListener('close', finish, { ...opts, once: true });
      dialog.addEventListener('cancel', event => { if (pending) event.preventDefault(); }, opts);
      dialog.addEventListener('click', event => { if (pending && event.target === dialog) event.stopImmediatePropagation(); }, { ...opts, capture: true });
      skip.addEventListener('click', () => { if (!pending) dialog.close(); }, opts);
      form.addEventListener('change', update, opts);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (pending || uncertain) return;
        const choices = selected();
        if (!choices.length) return;
        pending = true;
        form.setAttribute('aria-busy', 'true');
        submit.disabled = true;
        close.disabled = true;
        skip.disabled = true;
        submit.textContent = dialog.dataset.offerBusy;
        form.querySelectorAll('input').forEach(input => input.disabled = true);
        let responseAdded = false;
        try {
          const result = await request(`${config.add}.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: choices.map(input => ({ id: input.value, quantity: Number(input.dataset.offerQuantity) || 1 })), sections: ['guf-cart-drawer'], sections_url: window.location.pathname })
          });
          responseAdded = true;
          await syncCart(result);
          announce(config.added);
          pending = false;
          dialog.close();
        } catch {
          uncertain = true;
          try { await refreshCart(); } catch {}
          if (responseAdded) { pending = false; dialog.close(); return; }
          message.textContent = dialog.dataset.offerError;
          message.hidden = false;
          pending = false;
          form.removeAttribute('aria-busy');
          close.disabled = false;
          skip.disabled = false;
          submit.textContent = originalLabel;
          skip.focus();
        }
      }, opts);
      if (!openDialog('GufOffers', trigger)) finish();
    });
  }
  function busyProduct(form, busy) {
    const region = form.closest('[data-product-section]');
    form.setAttribute('aria-busy', String(busy));
    region?.querySelectorAll('[data-add-button],[data-sticky-add]').forEach(button => {
      if (busy) {
        button.dataset.originalDisabled = String(button.disabled);
        button.dataset.originalHtml = button.innerHTML;
        button.disabled = true;
        button.textContent = config.adding;
      } else {
        button.disabled = button.dataset.originalDisabled === 'true';
        if (button.dataset.originalHtml) button.innerHTML = button.dataset.originalHtml;
      }
    });
  }
  async function addProduct(form) {
    if (cartBusy || !form.reportValidity()) return;
    cartBusy = true;
    const data = new FormData(form);
    data.set('sections', 'guf-cart-drawer');
    data.set('sections_url', window.location.pathname);
    busyProduct(form, true);
    showError(form, '[data-product-error]', '');
    let added = false;
    try {
      const result = await request(`${config.add}.js`, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      added = true;
      if (!config.drawer && !document.getElementById('GufOffers')) { window.location.assign(config.cart); return; }
      await syncCart(result);
      announce(config.added);
      await offerProducts(form.querySelector('[data-add-button]'));
      if (!config.drawer) { window.location.assign(config.cart); return; }
      if (!openDialog('CartDialog', form.querySelector('[data-add-button]'))) window.location.assign(config.cart);
    } catch (error) {
      const message = added ? config.addedRefreshError : errorMessage(error, config.cartError);
      showError(form, '[data-product-error]', message);
      if (added) {
        const link = document.createElement('a');
        link.href = config.cart;
        link.className = 'text-link';
        link.textContent = 'View cart →';
        form.querySelector('[data-product-error]')?.append(' ', link);
      }
    } finally { cartBusy = false; busyProduct(form, false); }
  }
  async function changeCart(id, quantity, control) {
    if (cartBusy) return;
    cartBusy = true;
    const scope = control.closest('#CartDialog') || control.closest('[data-cart-page]');
    scope?.setAttribute('aria-busy', 'true');
    showError(scope, '[data-cart-error]', '');
    const buttons = [...(scope?.querySelectorAll('button,input[type=number]') || [])];
    buttons.forEach(button => { button.dataset.preBusyDisabled = String(button.disabled); button.disabled = true; });
    try {
      const result = await request(`${config.change}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity, sections: ['guf-cart-drawer'], sections_url: window.location.pathname })
      });
      if (document.querySelector('[data-cart-page]')) { window.location.assign(config.cart); return; }
      await syncCart(result);
      announce('Cart updated.');
    } catch (error) { showError(scope, '[data-cart-error]', errorMessage(error, config.cartError)); }
    finally {
      cartBusy = false;
      scope?.removeAttribute('aria-busy');
      buttons.forEach(button => { if (button.isConnected) button.disabled = button.dataset.preBusyDisabled === 'true'; });
    }
  }
  async function changeVariant(select) {
    const region = select.closest('[data-product-section]');
    if (!region) return;
    const url = new URL(region.dataset.productUrl, window.location.origin);
    url.searchParams.set('variant', select.value);
    const canonicalVariantURL = url.toString();
    url.searchParams.set('section_id', region.dataset.sectionId);
    region._variantRequest?.abort();
    const controller = new AbortController();
    region._variantRequest = controller;
    const form = region.querySelector('[data-product-form]');
    const selectedID = select.value;
    const buttons = [...region.querySelectorAll('[data-add-button],[data-sticky-add]')];
    buttons.forEach(button => { button.disabled = true; });
    form?.setAttribute('aria-busy', 'true');
    try {
      const html = await request(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (select.value !== selectedID) return;
      const incoming = parseHTML(html).querySelector('[data-product-section]');
      if (!incoming) throw new Error(config.error);
      const currentPlan = form?.querySelector('[name=selling_plan]')?.value;
      const currentQuantity = form?.querySelector('[name=quantity]')?.value;
      ['[data-product-price]', '.purchase-form-wrap', '[data-sticky-price]', '.product-stage', '[data-product-discount]'].forEach(selector => {
        const target = region.querySelector(selector);
        const source = incoming.querySelector(selector);
        if (target && source) target.innerHTML = source.innerHTML;
      });
      const sticky = region.querySelector('[data-sticky-add]');
      const newSticky = incoming.querySelector('[data-sticky-add]');
      if (sticky && newSticky) { sticky.innerHTML = newSticky.innerHTML; sticky.disabled = newSticky.disabled; }
      const newPlan = region.querySelector('[name=selling_plan]');
      if (newPlan && [...newPlan.options].some(option => option.value === currentPlan)) newPlan.value = currentPlan;
      const newQuantity = region.querySelector('[name=quantity]');
      if (newQuantity && Number(currentQuantity) >= Number(newQuantity.min || 1)) newQuantity.value = currentQuantity;
      history.replaceState({}, '', canonicalVariantURL);
      region.querySelector('[data-variant-select]')?.focus({ preventScroll: true });
      announce(region.querySelector('[data-product-price]')?.textContent.trim() || 'Option updated.');
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A normal navigation keeps server-side price and availability authoritative.
      window.location.assign(canonicalVariantURL);
    } finally { if (region._variantRequest === controller) form?.removeAttribute('aria-busy'); }
  }
  function initializeHero(hero) {
    if (initialized.has(hero)) return;
    initialized.add(hero);
    const videos = [...hero.querySelectorAll('.hero-video, .gg-studio-video, .gg-editorial-video')];
    if (!videos.length) return;
    const loaded = new WeakSet();
    const mobileViewport = window.matchMedia('(max-width: 700px)');
    const activeVideo = () => videos.find(video => getComputedStyle(video).display !== 'none') || videos[0];
    const autoplay = hero.hasAttribute('data-autoplay');
    videos.forEach(video => {
      if (autoplay) {
        video.controls = false;
        video.loop = true;
        video.muted = true;
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
        video.preload = 'none';
        video.querySelectorAll('source[src]').forEach(source => {
          source.dataset.src = source.getAttribute('src');
          source.removeAttribute('src');
        });
      } else {
        video.controls = true;
        video.loop = false;
        video.muted = false;
        video.removeAttribute('aria-hidden');
        video.removeAttribute('tabindex');
        video.preload = 'metadata';
      }
    });
    if (!autoplay) return;
    let visible = false;
    function allowed() {
      return !reducedMotion.matches && !navigator.connection?.saveData && !document.body.classList.contains('motion-off');
    }
    async function sync() {
      const video = activeVideo();
      videos.filter(item => item !== video).forEach(item => item.pause());
      if (!visible || document.hidden || !allowed()) { video.pause(); return; }
      if (!loaded.has(video)) {
        video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
        video.load(); loaded.add(video);
      }
      try { await video.play(); } catch { /* Poster and explicit play remain available. */ }
    }
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); }, { threshold: 0.1 });
    observer.observe(hero);
    const preferenceChanged = () => sync();
    const visibilityChanged = () => sync();
    const viewportChanged = () => sync();
    reducedMotion.addEventListener('change', preferenceChanged);
    mobileViewport.addEventListener('change', viewportChanged);
    document.addEventListener('visibilitychange', visibilityChanged);
    hero._cleanup = () => {
      observer.disconnect();
      reducedMotion.removeEventListener('change', preferenceChanged);
      mobileViewport.removeEventListener('change', viewportChanged);
      document.removeEventListener('visibilitychange', visibilityChanged);
      videos.forEach(video => video.pause());
    };
  }
  function initializeSticky(region) {
    if (initialized.has(region)) return;
    initialized.add(region);
    const band = region.querySelector('[data-purchase-band]');
    const sticky = region.querySelector('[data-sticky-purchase]');
    if (!band || !sticky) return;
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
    }, { threshold: 0 });
    observer.observe(band);
    region._cleanup = () => observer.disconnect();
  }
  function initializeCountry(select) {
    if (initialized.has(select)) return;
    initialized.add(select);
    const options = [...select.options];
    const selected = options.find(option => option.value === select.dataset.default || option.text === select.dataset.default);
    if (selected) select.value = selected.value;
    function provinces(preserve = false) {
      const target = document.getElementById(select.dataset.provinceId);
      if (!target) return;
      const values = JSON.parse(select.selectedOptions[0]?.dataset.provinces || '[]');
      target.replaceChildren(...values.map(([value, label]) => new Option(label, value)));
      target.closest('[data-province-container]').hidden = values.length === 0;
      if (preserve && [...target.options].some(option => option.value === target.dataset.default)) target.value = target.dataset.default;
    }
    select.addEventListener('change', () => provinces());
    provinces(true);
  }
  function formatAudioTime(value) {
    if (!Number.isFinite(value)) return '—:—';
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
  function initializeAudioTrack(track) {
    if (initialized.has(track)) return;
    initialized.add(track);
    const audio = track.querySelector('[data-audio]');
    const toggle = track.querySelector('[data-audio-toggle]');
    const progress = track.querySelector('[data-audio-progress]');
    const current = track.querySelector('[data-audio-current]');
    const duration = track.querySelector('[data-audio-duration]');
    if (!audio || !toggle || !progress) { if (toggle) toggle.disabled = true; return; }
    const syncState = () => {
      const playing = !audio.paused;
      track.classList.toggle('is-playing', playing);
      toggle.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ${track.querySelector('h3')?.textContent?.trim() || 'audio preview'}`);
    };
    const syncTime = () => {
      const ratio = audio.duration ? audio.currentTime / audio.duration * 100 : 0;
      progress.value = String(ratio);
      if (current) current.textContent = formatAudioTime(audio.currentTime);
      if (duration) duration.textContent = formatAudioTime(audio.duration);
    };
    toggle.addEventListener('click', () => {
      if (audio.paused) audio.play().catch(() => announce(config.error));
      else audio.pause();
    });
    progress.addEventListener('input', () => {
      if (audio.duration) audio.currentTime = Number(progress.value) / 100 * audio.duration;
    });
    audio.addEventListener('loadedmetadata', () => { progress.disabled = false; syncTime(); });
    audio.addEventListener('timeupdate', syncTime);
    audio.addEventListener('play', syncState);
    audio.addEventListener('pause', syncState);
    audio.addEventListener('ended', syncState);
    if (audio.readyState >= 1) { progress.disabled = false; syncTime(); }
    syncState();
  }
  function initialize(scope = document) {
    scope.querySelectorAll('dialog').forEach(initializeDialog);
    scope.querySelectorAll('[data-hero]').forEach(initializeHero);
    scope.querySelectorAll('[data-product-section]').forEach(initializeSticky);
    scope.querySelectorAll('[data-country-select]').forEach(initializeCountry);
    scope.querySelectorAll('[data-audio-track]').forEach(initializeAudioTrack);
    // Native form feedback must be visible even if it returned inside a disclosure.
    scope.querySelectorAll('details .form-errors, details [role=status]').forEach(node => {
      if (node.textContent.trim()) node.closest('details').open = true;
    });
    if (location.hash.toLowerCase() === '#recover') document.getElementById('Recover')?.setAttribute('open', '');
  }
  document.addEventListener('click', async event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const open = target.closest('[data-dialog-open]');
    if (open && !(open instanceof HTMLAnchorElement && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) && openDialog(open.dataset.dialogOpen, open)) { event.preventDefault(); return; }
    const close = target.closest('[data-dialog-close]');
    if (close) { event.preventDefault(); close.closest('dialog')?.close(); return; }
    const cart = target.closest('[data-cart-open]');
    if (cart && config.drawer && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      if (!document.getElementById('CartDialog')?.showModal) return;
      event.preventDefault();
      openDialog('CartDialog', cart);
      try { await refreshCart(); } catch { showError(document.getElementById('CartDialog'), '[data-cart-error]', config.cartError); }
      return;
    }
    const remove = target.closest('[data-cart-remove]');
    if (remove && !event.metaKey && !event.ctrlKey) { event.preventDefault(); changeCart(remove.dataset.cartRemove, 0, remove); return; }
    const next = target.closest('[data-next-section]');
    if (next) {
      const section = next.closest('.shopify-section')?.nextElementSibling;
      if (section) { event.preventDefault(); section.scrollIntoView({ behavior: reducedMotion.matches || document.body.classList.contains('motion-off') ? 'instant' : 'smooth' }); }
    }
    const share = target.closest('[data-share-url]');
    if (share) {
      try {
        if (navigator.share) await navigator.share({ title: share.dataset.shareTitle, url: share.dataset.shareUrl });
        else { await navigator.clipboard.writeText(share.dataset.shareUrl); announce(config.copied); share.textContent = config.copied; }
      } catch (error) { if (error.name !== 'AbortError') announce(config.error); }
    }
    if (target.closest('[data-confirm-delete]') && !window.confirm('Delete this address?')) event.preventDefault();
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-product-form]')) { event.preventDefault(); addProduct(form); }
    else if (form.matches('[data-cart-change]')) {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const data = new FormData(form);
      const quantity = Number(data.get('quantity'));
      if (Number.isInteger(quantity) && quantity >= 0) changeCart(String(data.get('id')), quantity, form);
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-variant-select]')) changeVariant(event.target);
  });
  document.addEventListener('input', event => {
    const input = event.target;
    if (input.matches('[data-comparison] input[type=range]')) {
      input.closest('[data-comparison]').style.setProperty('--split', `${input.value}%`);
      input.setAttribute('aria-valuetext', `${input.value}% ${input.dataset.afterLabel}`);
    }
    if (!input.matches('[data-predictive-search] input[type=search]')) return;
    clearTimeout(searchTimer);
    searchRequest?.abort();
    const results = document.getElementById('PredictiveResults');
    const query = input.value.trim();
    if (query.length < 2) { results.replaceChildren(); results.removeAttribute('aria-busy'); return; }
    searchTimer = setTimeout(async () => {
      const controller = new AbortController();
      searchRequest = controller;
      results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(config.search, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,article,page');
        url.searchParams.set('resources[limit]', '4');
        url.searchParams.set('section_id', 'guf-predictive-search');
        const html = await request(url.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
        if (input.value.trim() !== query) return;
        const section = parseHTML(html).querySelector('#shopify-section-guf-predictive-search');
        results.innerHTML = section ? section.innerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') results.textContent = 'Press Enter to search all products and articles.';
      } finally { if (searchRequest === controller) results.removeAttribute('aria-busy'); }
    }, 220);
  });
  document.addEventListener('keydown', event => {
    const dialog = document.getElementById('SearchDialog');
    if (!dialog?.open || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const targets = [...dialog.querySelectorAll('input[type=search], #PredictiveResults a')];
    if (!targets.length) return;
    const index = targets.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? (index + 1) % targets.length : (index - 1 + targets.length) % targets.length;
    event.preventDefault(); targets[next].focus();
  });
  document.addEventListener('play', event => {
    if (event.target instanceof HTMLAudioElement) document.querySelectorAll('audio').forEach(audio => { if (audio !== event.target) audio.pause(); });
  }, true);
  let progressScheduled = false;
  function updateProgress() {
    progressScheduled = false;
    const article = document.querySelector('[data-article]');
    const progress = article?.querySelector('[data-reading-progress]');
    if (!progress || reducedMotion.matches || document.body.classList.contains('motion-off')) return;
    const bounds = article.getBoundingClientRect();
    const value = Math.max(0, Math.min(1, -bounds.top / Math.max(1, bounds.height - window.innerHeight)));
    progress.style.transform = `scaleX(${value})`;
  }
  if (document.querySelector('[data-article]')) {
    window.addEventListener('scroll', () => { if (!progressScheduled) { progressScheduled = true; requestAnimationFrame(updateProgress); } }, { passive: true });
    updateProgress();
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-hero],[data-product-section]').forEach(node => node._cleanup?.());
  });
  window.addEventListener('pageshow', event => { if (event.persisted && config.drawer) refreshCart().catch(() => {}); });
  initialize();
})();
